import { Box, Palette, useTheme } from '@mui/material';
import { Ban, CheckCircle, Search } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';

import Breadcrumbs from 'components/breadcrumbs';
import FilterSidebar from 'components/filter-sidebar';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import Pagination from 'components/pagination';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import SelfServiceTabsHeader from '../../components/self-service-tabs-header';
import CreateTreatment from './components/create-treatment';
import EditTreatment from './components/edit-treatment';

import { Treatment } from 'views/self-service/features/treatment-catalog/types/treatment.type';

import Paper from 'components/paper';
import { usePagination } from 'hooks/use-pagination';
import { loadState, saveState } from 'utils/storage';
import { TreatmentCatalogProvider, useTreatmentCatalogState } from './context/treatment-catalog-context';

const getIcon = (hidden: boolean, color: Palette) => {
  const Icon = hidden ? Ban : CheckCircle;
  return <Icon color={hidden ? color.darkBlue.o25 : color.bluViu.main} size={20} strokeWidth={3} />;
};

type Filters = {
  searchValue: string;
  showActiveTreatments: boolean;
  showInactiveTreatments: boolean;
};

const defaultValues: Filters = {
  searchValue: '',
  showActiveTreatments: true,
  showInactiveTreatments: false,
};

type TreatmentCatalogProps = {};

const PAGE_NAME_ID = 'selfService.treatmentCatalog';

const TreatmentCatalog: FC<TreatmentCatalogProps> = ({}) => {
  const intl = useIntl();
  const theme = useTheme();
  const { treatments, isLoading, treatmentCodes } = useTreatmentCatalogState();

  // #region PAGINATION
  const PAGINATION_LIMIT = 15;
  // #endregion PAGINATION

  // #region STATE
  const { currentPage, from, to, handlePagination } = usePagination(PAGINATION_LIMIT);
  const [filters, setFilters] = useState<Filters>(loadState({ key: 'treatmentFilters' }) || defaultValues);
  // #endregion STATE

  // #region OPTIONS
  const treatmentCatalogListOptions: HeadCell[] = [
    { id: 'name', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.name` }) },
    { id: 'code', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.code` }) },
    { id: 'type', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.type` }) },
    { id: 'brand', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.brand` }) },
    { id: 'price', align: 'right', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.price` }) },
    { id: 'treatmentCategory', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.treatmentCategory` }) },
    { id: 'createdBy', align: 'left', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.createdBy` }) },
    { id: 'hidden', align: 'center', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.active` }) },
    {
      id: 'actions',
      label: '',
      autoWidth: true,
    },
  ];
  // #endregion OPTIONS

  // #region HANDLERS

  const resetStates = () => {
    updateFilters(defaultValues);
  };

  const updateFilters = (newFilters: Filters) => {
    setFilters(newFilters);
    saveState({ key: 'treatmentFilters', state: newFilters });
  };
  // #endregion HANDLRES

  // #region RENDERS
  const treatmentsList = useMemo(() => {
    let rows: Treatment[] | undefined = treatments;
    rows = _.chain(treatments)
      .filter((treatment) => (filters.showActiveTreatments ? true : treatment.hidden))
      .filter((treatment) => (filters.showInactiveTreatments ? true : !treatment.hidden))
      .filter((treatment) => treatment.name.toLowerCase().includes(filters.searchValue))
      .sortBy((treatment) => treatment.name.toLowerCase())
      .value();

    return rows;
  }, [filters, treatments]);
  // #endregion RENDERS

  return (
    <>
      <SelfServiceTabsHeader utils={<Breadcrumbs />} />
      <Box display={'flex'} justifyContent={'flex-end'}>
        <CreateTreatment treatmentCodes={treatmentCodes} />
      </Box>
      <Paper transparent>
        <FilterSidebar
          sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          showClearFilter={!!filters.searchValue || !filters.showActiveTreatments || filters.showInactiveTreatments}
          clearFilters={() => {
            resetStates();
          }}>
          <Box display={'flex'} flexDirection={'row'} gap={4}>
            <Switch
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showActive` })}
              value={filters.showActiveTreatments}
              onChange={() => {
                handlePagination(1);
                let newFilters = { ...filters, showActiveTreatments: !filters.showActiveTreatments };
                updateFilters(newFilters);
              }}
            />
            <Switch
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.filters.showInactive` })}
              value={filters.showInactiveTreatments}
              onChange={() => {
                handlePagination(1);
                let newFilters = { ...filters, showInactiveTreatments: !filters.showInactiveTreatments };
                updateFilters(newFilters);
              }}
            />
          </Box>

          <TextField
            value={filters.searchValue}
            label={intl.formatMessage({ id: 'general.search' })}
            onChange={(event) => {
              handlePagination(1);
              let newFilters = { ...filters, searchValue: event.target.value as string };
              updateFilters(newFilters);
            }}
            endAdornment={<Search size={18} />}
          />
        </FilterSidebar>
        <List
          isLoading={isLoading}
          noResult={{
            show: !treatmentsList.length,
            text: intl.formatMessage({ id: 'general.noResults' }),
          }}
          options={treatmentCatalogListOptions}>
          {treatmentsList.slice(from, to).map((treatment, idx) => (
            <ListRow key={treatment.id}>
              <ListCell align="left">{treatment.name}</ListCell>
              <ListCell align="left" capitalize>
                {treatment.code}
              </ListCell>
              <ListCell align="left" capitalize>
                <FormattedMessage id={`${PAGE_NAME_ID}.types.${treatment.type}`} />
              </ListCell>
              <ListCell align="left" capitalize>
                {treatment.brand}
              </ListCell>
              <ListCell align="right">{treatment.price}</ListCell>
              <ListCell align="left" capitalize>
                <FormattedMessage id={`${PAGE_NAME_ID}.types.${treatment.treatmentCategory}`} />
              </ListCell>
              <ListCell align="left">{treatment.createdBy}</ListCell>
              <ListCell align="center">
                {getIcon(treatment.hidden, theme.palette)}
              </ListCell>
              <ListCell>
                <EditTreatment treatments={treatments} idx={idx} treatment={treatment} />
              </ListCell>
            </ListRow>
          ))}
        </List>
        {treatmentsList.length > PAGINATION_LIMIT && (
          <Pagination
            currentPage={currentPage}
            pages={Math.ceil(treatmentsList.length / PAGINATION_LIMIT)}
            onClick={handlePagination}
          />
        )}
      </Paper>
    </>
  );
};

const TreatmentCatalogContainer: React.FC<{}> = ({}) => {
  return (
    <TreatmentCatalogProvider>
      <TreatmentCatalog />
    </TreatmentCatalogProvider>
  );
};

export default TreatmentCatalogContainer;
